import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { SocialAnalyticsDashboard } from "./SocialAnalyticsDashboard";
import { Badge } from "@/components/ui/badge";
import { TrendingUp, TrendingDown, Users, Heart, MessageCircle, Eye, Award, Download } from "lucide-react";
import { ConorbitLoader } from "@/components/ui/ConorbitLoader";
import { formatDistanceToNow } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import { useOrgPermissions } from "@/hooks/useOrgPermissions";

// ── Regional Insights Tab ──────────────────────────────────────────────────

function RegionalInsightsTab({ orgId }: { orgId: string | null }) {
  const { data: sovData, isLoading: sovLoading } = useQuery({
    queryKey: ['sov-snapshots', orgId],
    enabled: !!orgId,
    queryFn: async () => {
      const { data } = await (supabase
        .from('sov_snapshots' as any)
        .select('org_mentions, competitor_mentions, org_share_pct, period_start, period_end, computed_at')
        .eq('org_id', orgId!)
        .order('computed_at', { ascending: false })
        .limit(12) as any)
      return (data ?? []) as any[]
    },
  })

  const { data: coverageData, isLoading: covLoading } = useQuery({
    queryKey: ['coverage-articles-regional', orgId],
    enabled: !!orgId,
    queryFn: async () => {
      const { data } = await (supabase
        .from('coverage_articles' as any)
        .select('id, headline, source_name, sentiment_label, sentiment_score, published_at')
        .eq('org_id', orgId!)
        .order('published_at', { ascending: false })
        .limit(10) as any)
      return (data ?? []) as any[]
    },
  })

  const isLoading = sovLoading || covLoading
  const hasSov = (sovData ?? []).length > 0
  const hasCoverage = (coverageData ?? []).length > 0

  const sentimentColors: Record<string, string> = {
    positive: 'text-emerald-600 bg-emerald-50',
    negative: 'text-red-600 bg-red-50',
    neutral:  'text-zinc-600 bg-zinc-100',
  }

  if (isLoading) return <ConorbitLoader variant="inline" />;

  return (
    <div className="space-y-6">
      {/* Share of Voice */}
      <Card className="p-6">
        <div className="flex items-center gap-2 mb-4">
          <Award className="w-5 h-5 text-primary" />
          <h3 className="font-semibold">Share of Voice</h3>
        </div>
        {hasSov ? (
          <div className="space-y-3">
            {(sovData ?? []).slice(0, 6).map((snap: any, i: number) => (
              <div key={i} className="space-y-1">
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>{new Date(snap.period_start).toLocaleDateString('en-GB', { month: 'short', day: 'numeric' })} – {new Date(snap.period_end).toLocaleDateString('en-GB', { month: 'short', day: 'numeric' })}</span>
                  <span className="font-medium text-foreground">{(snap.org_share_pct ?? 0).toFixed(1)}% SOV</span>
                </div>
                <div className="h-2 bg-muted rounded-full overflow-hidden">
                  <div
                    className="h-full bg-primary rounded-full transition-all"
                    style={{ width: `${Math.min(snap.org_share_pct ?? 0, 100)}%` }}
                  />
                </div>
                <div className="flex gap-4 text-[11px] text-muted-foreground">
                  <span>Your mentions: {snap.org_mentions}</span>
                  <span>Competitor mentions: {snap.competitor_mentions}</span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground py-4 text-center">
            No Share of Voice data yet. SOV is computed nightly once social listening topics are configured.
          </p>
        )}
      </Card>

      {/* Recent Coverage */}
      <Card className="p-6">
        <h3 className="font-semibold mb-4">Recent Media Coverage</h3>
        {hasCoverage ? (
          <div className="space-y-3">
            {(coverageData ?? []).map((article: any) => (
              <div key={article.id} className="border border-border rounded-lg p-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{article.headline || 'Untitled'}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{article.source_name ?? 'Unknown source'}</p>
                  </div>
                  {article.sentiment_label && (
                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full shrink-0 ${sentimentColors[article.sentiment_label] ?? sentimentColors.neutral}`}>
                      {article.sentiment_label}
                    </span>
                  )}
                </div>
                {article.published_at && (
                  <p className="text-[11px] text-muted-foreground mt-1">
                    {formatDistanceToNow(new Date(article.published_at), { addSuffix: true })}
                  </p>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground py-4 text-center">
            No coverage articles scored yet. Add coverage articles in the Coverage section to see sentiment analysis here.
          </p>
        )}
      </Card>
    </div>
  )
}

// ── Platform colours ───────────────────────────────────────────────────────

const platformColors = {
  instagram: 'from-purple-500 to-pink-500',
  twitter:   'from-blue-400 to-blue-500',
  linkedin:  'from-blue-600 to-blue-700',
  facebook:  'from-blue-500 to-blue-600',
  unknown:   'from-gray-400 to-gray-500',
};

const formatNumber = (n: number) => {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000)     return `${(n / 1_000).toFixed(1)}K`;
  return n.toString();
};

function exportToCSV(posts: any[], platformStats: any[], filename: string) {
  const rows: string[][] = []

  rows.push(['Platform Stats'])
  rows.push(['Platform', 'Posts', 'Followers', 'Total Reach', 'Avg Engagement %', 'Likes', 'Comments', 'Shares'])
  for (const p of platformStats) {
    rows.push([p.platform, p.postsCount, p.followers, p.totalReach, p.engagementRate.toFixed(2), p.likes, p.comments, p.shares].map(String))
  }

  rows.push([])
  rows.push(['Post Detail'])
  rows.push(['Published At', 'Platform', 'Content', 'Reach', 'Likes', 'Comments', 'Shares', 'Engagement Rate'])
  for (const post of posts) {
    const pa = post.post_analytics?.[0]
    rows.push([
      post.published_at ?? '',
      post.social_accounts?.platform ?? '',
      (post.content ?? '').replace(/"/g, '""'),
      String(pa?.reach_count ?? 0),
      String(pa?.likes_count ?? 0),
      String(pa?.comments_count ?? 0),
      String(pa?.shares_count ?? 0),
      String(pa?.engagement_rate ?? 0),
    ])
  }

  const csv = rows.map(r => r.map(v => `"${v}"`).join(',')).join('\n')
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}

export const AnalyticsView = ({ showHeader = true }: { showHeader?: boolean }) => {
  const { orgId } = useOrgPermissions();
  const [timeRange, setTimeRange] = useState<'7d'|'30d'|'90d'>('30d');

  const since = new Date(
    Date.now() - ({ '7d': 7, '30d': 30, '90d': 90 } as Record<string, number>)[timeRange] * 86400000
  ).toISOString();

  // Published posts joined with their social account (for platform) and analytics
  const { data: postsData, isLoading } = useQuery({
    queryKey: ['analytics-posts', orgId, timeRange],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];

      let query = supabase
        .from('posts')
        .select(`
          id, content, published_at, social_account_id,
          social_accounts!posts_social_account_id_fkey(platform, follower_count, followers_count),
          post_analytics(likes_count, comments_count, shares_count, reach_count, impressions_count, engagement_rate)
        `)
        .eq('user_id', user.id)
        .eq('status', 'published')
        .gte('published_at', since)
        .order('published_at', { ascending: false });

      if (orgId) query = (query as any).eq('org_id', orgId);
      const { data } = await (query as any);
      return (data ?? []) as any[];
    },
  });

  const posts = postsData ?? [];

  // Aggregate per-platform stats
  const platformMap = new Map<string, {
    platform: string; followers: number; likes: number;
    comments: number; shares: number; reach: number;
    engagementRates: number[]; postsCount: number;
  }>();

  for (const post of posts) {
    const platform: string = post.social_accounts?.platform ?? 'unknown';
    const followers: number = post.social_accounts?.followers_count
      ?? post.social_accounts?.follower_count ?? 0;
    const pa = post.post_analytics?.[0];

    if (!platformMap.has(platform)) {
      platformMap.set(platform, {
        platform, followers, likes: 0, comments: 0,
        shares: 0, reach: 0, engagementRates: [], postsCount: 0,
      });
    }
    const row = platformMap.get(platform)!;
    row.postsCount++;
    if (pa) {
      row.likes    += pa.likes_count    ?? 0;
      row.comments += pa.comments_count ?? 0;
      row.shares   += pa.shares_count   ?? 0;
      row.reach    += pa.reach_count    ?? 0;
      if (pa.engagement_rate != null) row.engagementRates.push(pa.engagement_rate);
    }
    // keep the highest follower count seen for this platform
    if (followers > row.followers) row.followers = followers;
  }

  const platformStats = Array.from(platformMap.values()).map(p => ({
    ...p,
    engagementRate: p.engagementRates.length
      ? p.engagementRates.reduce((a, b) => a + b, 0) / p.engagementRates.length
      : 0,
    totalReach: p.reach,
  }));

  // Top posts by total engagement (likes + comments + shares)
  const topPosts = [...posts]
    .filter(p => p.post_analytics?.[0])
    .map(p => {
      const pa = p.post_analytics[0];
      return {
        id:          p.id,
        platform:    p.social_accounts?.platform ?? 'unknown',
        content:     p.content ?? '',
        publishedAt: p.published_at,
        engagement:  (pa.likes_count ?? 0) + (pa.comments_count ?? 0) + (pa.shares_count ?? 0),
        reach:       pa.reach_count ?? 0,
        comments:    pa.comments_count ?? 0,
      };
    })
    .sort((a, b) => b.engagement - a.engagement)
    .slice(0, 5);

  const totalFollowers = platformStats.reduce((s, p) => s + p.followers, 0);
  const totalFollowersForWeight = platformStats.reduce((s, p) => s + Math.max(p.followers, 1), 0);
  const avgEngagement = totalFollowersForWeight > 0
    ? platformStats.reduce((s, p) => s + p.engagementRate * Math.max(p.followers, 1), 0) / totalFollowersForWeight
    : 0;
  const totalReach      = platformStats.reduce((s, p) => s + p.totalReach, 0);
  const totalComments   = topPosts.reduce((s, p) => s + p.comments, 0);

  if (isLoading) return <ConorbitLoader variant="inline" />;

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        {showHeader ? (
          <div>
            <h2 className="text-2xl font-bold mb-2">Analytics Dashboard</h2>
            <p className="text-muted-foreground">Track your performance across all platforms</p>
          </div>
        ) : <div />}
        <button
          onClick={() => exportToCSV(posts, platformStats, `analytics-${timeRange}-${new Date().toISOString().slice(0,10)}.csv`)}
          disabled={posts.length === 0}
          className="flex items-center gap-2 px-3 py-2 rounded-lg border border-border text-sm font-medium hover:bg-muted transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          title="Download analytics as CSV"
        >
          <Download className="w-4 h-4" />
          Export CSV
        </button>
      </div>

      {/* Time range selector */}
      <div className="flex items-center gap-1 mb-4">
        {(['7d', '30d', '90d'] as const).map(r => (
          <button
            key={r}
            onClick={() => setTimeRange(r)}
            className={`px-3 py-1.5 rounded-lg text-[12px] font-medium transition-colors ${
              timeRange === r
                ? 'bg-[#0D2240] text-white'
                : 'text-muted-foreground hover:bg-muted'
            }`}
          >
            Last {r.replace('d', ' days')}
          </button>
        ))}
      </div>

      <Tabs defaultValue="social" className="w-full">
        <TabsList>
          <TabsTrigger value="social">Social Media</TabsTrigger>
          <TabsTrigger value="overview">Coverage Overview</TabsTrigger>
          <TabsTrigger value="platforms">By Platform</TabsTrigger>
          <TabsTrigger value="content">Top Content</TabsTrigger>
          <TabsTrigger value="regional">Regional Insights</TabsTrigger>
        </TabsList>

        {/* Social Media Analytics — real platform data */}
        <TabsContent value="social" className="mt-6">
          <SocialAnalyticsDashboard />
        </TabsContent>

        {/* Overview */}
        <TabsContent value="overview" className="space-y-6 mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="p-6">
              <Users className="w-8 h-8 text-primary mb-4" />
              <p className="text-justify text-sm text-muted-foreground">Total Followers</p>
              <p className="text-3xl font-bold mt-1">{formatNumber(totalFollowers)}</p>
            </Card>
            <Card className="p-6">
              <Heart className="w-8 h-8 text-primary mb-4" />
              <p className="text-justify text-sm text-muted-foreground">Avg Engagement</p>
              <p className="text-3xl font-bold mt-1">{avgEngagement.toFixed(1)}%</p>
            </Card>
            <Card className="p-6">
              <Eye className="w-8 h-8 text-primary mb-4" />
              <p className="text-justify text-sm text-muted-foreground">Total Reach</p>
              <p className="text-3xl font-bold mt-1">{formatNumber(totalReach)}</p>
            </Card>
            <Card className="p-6">
              <MessageCircle className="w-8 h-8 text-primary mb-4" />
              <p className="text-justify text-sm text-muted-foreground">Total Comments</p>
              <p className="text-3xl font-bold mt-1">{formatNumber(totalComments)}</p>
            </Card>
          </div>

          {posts.length === 0 && (
            <Card className="p-8 text-center">
              <p className="text-justify text-muted-foreground">
                No published posts yet. Publish content to see your analytics here.
              </p>
            </Card>
          )}
        </TabsContent>

        {/* By Platform */}
        <TabsContent value="platforms" className="space-y-4 mt-6">
          {platformStats.length === 0 ? (
            <Card className="p-8 text-center">
              <p className="text-justify text-muted-foreground">
                No platform data available. Publish posts to see per-platform analytics.
              </p>
            </Card>
          ) : (
            platformStats.map((platform) => (
              <Card key={platform.platform} className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-lg bg-gradient-to-br ${
                      platformColors[platform.platform as keyof typeof platformColors] ?? platformColors.unknown
                    } flex items-center justify-center text-white font-bold uppercase`}>
                      {platform.platform.charAt(0)}
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold capitalize">{platform.platform}</h3>
                      <p className="text-justify text-sm text-muted-foreground">{platform.postsCount} posts</p>
                    </div>
                  </div>
                  {platform.engagementRate > 3
                    ? <TrendingUp className="w-5 h-5 text-success" />
                    : <TrendingDown className="w-5 h-5 text-muted-foreground" />
                  }
                </div>
                <div className="grid grid-cols-3 gap-6 mt-6">
                  <div>
                    <p className="text-justify text-sm text-muted-foreground mb-1">Followers</p>
                    <p className="text-2xl font-bold">{formatNumber(platform.followers)}</p>
                  </div>
                  <div>
                    <p className="text-justify text-sm text-muted-foreground mb-1">Total Reach</p>
                    <p className="text-2xl font-bold">{formatNumber(platform.totalReach)}</p>
                  </div>
                  <div>
                    <p className="text-justify text-sm text-muted-foreground mb-1">Avg Engagement</p>
                    <p className="text-2xl font-bold">{platform.engagementRate.toFixed(1)}%</p>
                  </div>
                </div>
              </Card>
            ))
          )}
        </TabsContent>

        {/* Top Content */}
        <TabsContent value="content" className="space-y-4 mt-6">
          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-4">Top Performing Posts</h3>
            {topPosts.length === 0 ? (
              <p className="text-justify text-center text-muted-foreground py-8">
                No posts with analytics yet. Analytics sync runs every 30 minutes after publishing.
              </p>
            ) : (
              <div className="space-y-4">
                {topPosts.map((post, index) => (
                  <div key={post.id} className="border border-border rounded-lg p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center font-bold text-sm">
                          #{index + 1}
                        </div>
                        <div>
                          <Badge variant="secondary" className="capitalize">{post.platform}</Badge>
                          <p className="text-justify text-xs text-muted-foreground mt-1">
                            {post.publishedAt
                              ? formatDistanceToNow(new Date(post.publishedAt), { addSuffix: true })
                              : 'Unknown'}
                          </p>
                        </div>
                      </div>
                    </div>
                    <p className="text-justify text-sm mb-4">
                      {post.content.substring(0, 100)}{post.content.length > 100 ? '...' : ''}
                    </p>
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div>
                        <p className="text-justify text-muted-foreground">Engagement</p>
                        <p className="text-justify font-semibold">{post.engagement.toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-justify text-muted-foreground">Reach</p>
                        <p className="text-justify font-semibold">{post.reach.toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-justify text-muted-foreground">Comments</p>
                        <p className="text-justify font-semibold">{post.comments.toLocaleString()}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="regional" className="space-y-6 mt-6">
          <RegionalInsightsTab orgId={orgId} />
        </TabsContent>
      </Tabs>
    </div>
  );
};
